import { useEffect, useState, useRef } from "react";

/**
 * AchievementImage — renders an achievement image, and if the achievement
 * has more than one image (primary `src` + `additionalImages`), cycles
 * through them with a 3D card flip every 15 seconds.
 *
 * Props:
 *  - src: primary image URL
 *  - additionalImages: optional array of extra image URLs (0-4 items)
 *  - alt: img alt text
 *  - className: applied to the outer container AND each rendered <img>
 *  - intervalMs: override the 15s cycle (used mostly for tests)
 *  - onClick: optional click handler
 *  - style: inline style for the outer container
 *
 * Single-image achievements render as a plain <img> — no wrapper overhead,
 * no animation, no flip. Multi-image achievements gain the flip behavior
 * automatically.
 */
export default function AchievementImage({
  src,
  additionalImages,
  alt = "",
  className = "",
  intervalMs = 15000,
  onClick,
  style,
  ...rest
}) {
  const allImages = [src, ...((additionalImages || []).filter(Boolean))].filter(Boolean);

  const [index, setIndex] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const hoveredRef = useRef(false);

  useEffect(() => {
    if (allImages.length <= 1) return;
    let cancelled = false;
    const timer = setInterval(() => {
      if (hoveredRef.current) return; // pause while hovered
      setFlipping(true);
      // Halfway through the 700ms flip, swap the underlying image so the
      // back-face reveals the new content.
      setTimeout(() => {
        if (cancelled) return;
        setIndex((prev) => (prev + 1) % allImages.length);
      }, 350);
      setTimeout(() => {
        if (cancelled) return;
        setFlipping(false);
      }, 700);
    }, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [allImages.length, intervalMs]);

  if (allImages.length === 0) {
    return null;
  }

  if (allImages.length === 1) {
    return (
      <img
        src={allImages[0]}
        alt={alt}
        className={className}
        onClick={onClick}
        style={style}
        {...rest}
      />
    );
  }

  return (
    <div
      className={className}
      style={{
        position: "relative",
        display: "inline-block",
        perspective: 800,
        cursor: onClick ? "pointer" : undefined,
        ...style,
      }}
      onClick={onClick}
      onMouseEnter={() => { hoveredRef.current = true; }}
      onMouseLeave={() => { hoveredRef.current = false; }}
      data-testid="achievement-image-rotating"
      data-image-count={allImages.length}
      title={alt}
    >
      <img
        src={allImages[index]}
        alt={alt}
        className={className}
        style={{
          transition: "transform 700ms ease-in-out",
          transformStyle: "preserve-3d",
          transform: flipping ? "rotateY(90deg)" : "rotateY(0deg)",
          display: "block",
          width: "100%",
          height: "100%",
        }}
        {...rest}
      />
      {/* Tiny indicator dot cluster so users know rotation is happening */}
      <div
        style={{
          position: "absolute",
          bottom: 2,
          right: 3,
          display: "flex",
          gap: 2,
          pointerEvents: "none",
        }}
      >
        {allImages.map((_, i) => (
          <span
            key={i}
            style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: i === index ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)",
              boxShadow: "0 0 2px rgba(0,0,0,0.6)",
            }}
          />
        ))}
      </div>
    </div>
  );
}
